/**
 * Создает обертку над функцией, которая вызывает исходную функцию не более 1 раза в `waitMs` миллисекунд.
 * Первый вызов исходной функции происходит сразу.
 *
 * @param fn Функция для троттлинга
 * @param waitMs Количество миллисекунд до вызова функции (должно быть положительным числом)
 * @typeParam Fn Тип функции
 * @returns Функция-обертка. Функция содержит метод `cancel`, позволяющий отменить отложенный вызов исходной функции
 * @example
 * ```ts
 * // Троттлинг функции обработки скролла
 * const handleScroll = throttle(() => {
 *   console.log('Scrolling...');
 * }, 100);
 *
 * // При быстром скролле функция будет вызываться не чаще чем раз в 100мс
 * window.addEventListener('scroll', handleScroll);
 *
 * // Отмена отложенного вызова
 * handleScroll.cancel();
 *
 * // Троттлинг функции отправки аналитики
 * const sendAnalytics = throttle((event: string) => {
 *   analytics.track(event);
 * }, 500);
 *
 * // Троттлинг обработчика изменения размера окна
 * const handleResize = throttle(() => {
 *   console.log('Window resized:', window.innerWidth, window.innerHeight);
 * }, 250);
 *
 * window.addEventListener('resize', handleResize);
 *
 * // Троттлинг функции обработки перемещения мыши
 * const handleMouseMove = throttle((event: MouseEvent) => {
 *   updateTooltipPosition(event.clientX, event.clientY);
 * }, 16); // ~60fps
 *
 * document.addEventListener('mousemove', handleMouseMove);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any требуется в extends
export function throttle(fn, waitMs) {
    let lastFunc;
    let lastRan;
    function throttledFn(...args) {
        if (lastRan === undefined) {
            fn(...args);
            lastRan = performance.now();
        }
        else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(function () {
                if (performance.now() - lastRan >= waitMs) {
                    fn(...args);
                    lastRan = performance.now();
                }
            }, waitMs - (performance.now() - lastRan));
        }
    }
    Object.assign(throttledFn, {
        cancel() {
            clearTimeout(lastFunc);
        },
    });
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return throttledFn;
}
